import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  LinkOff as LinkOffIcon,
  Refresh as RefreshIcon,
  VpnKey as KeyIcon,
} from '@mui/icons-material';
import { callFunction } from '../lib/integrationApi';

export default function ProfileIntegrations() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [student, setStudent] = useState(null);
  const [code, setCode] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  useEffect(() => {
    const intervalId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await callFunction('srbhub-links', { method: 'GET' });
      const own = (data?.students || [])[0] || null;
      setStudent(own);
      if (own?.active_code) {
        setCode(own.active_code);
        setExpiresAt(own.code_expires_at);
      }
    } catch (err) {
      showSnackbar(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('ProfileIntegrations mount');
    loadData();
  }, []);

  const remainingSeconds = useMemo(() => {
    if (!expiresAt) return 0;
    return Math.max(0, Math.floor((new Date(expiresAt).getTime() - now) / 1000));
  }, [expiresAt, now]);

  const countdown = `${String(Math.floor(remainingSeconds / 60)).padStart(2, '0')}:${String(
    remainingSeconds % 60,
  ).padStart(2, '0')}`;

  const handleGenerate = async () => {
    setBusy(true);
    try {
      const data = await callFunction('srbhub-link-generate', { method: 'POST', body: {} });
      setCode(data?.code ?? null);
      setExpiresAt(data?.expires_at ?? null);
      showSnackbar('Cod generat. Introdu-l în SRBHub.');
      loadData();
    } catch (err) {
      showSnackbar(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showSnackbar('Copierea a eșuat. Selectează manual codul.', 'warning');
    }
  };

  const handleRevoke = async () => {
    if (!student?.link_id) return;
    setBusy(true);
    try {
      await callFunction('srbhub-link-revoke', { method: 'POST', body: { link_id: student.link_id } });
      showSnackbar('Link revocat.');
      setCode(null);
      setExpiresAt(null);
      loadData();
    } catch (err) {
      showSnackbar(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const isLinked = student?.status === 'active';
  const isRevoked = student?.status === 'revoked';

  return (
    <Container maxWidth="md" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Integrări SRBHub
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Leagă contul de SRBHub pentru a primi automat creditele din cluburi.
        </Typography>
      </Box>

      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <KeyIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Cod de legătură
            </Typography>
          </Box>

          {code ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box
                sx={{
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: '#f5f3ff',
                  border: '1px dashed',
                  borderColor: 'primary.main',
                }}
              >
                <Typography variant="h4" fontFamily="monospace" fontWeight="bold" letterSpacing={4}>
                  {code}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color={remainingSeconds > 0 ? 'text.secondary' : 'error'}>
                  {remainingSeconds > 0 ? `Expiră în ${countdown}` : 'Cod expirat'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CopyIcon />}
                    onClick={handleCopy}
                    disabled={remainingSeconds === 0}
                  >
                    {copied ? 'Copiat!' : 'Copiază'}
                  </Button>
                  <Button size="small" onClick={handleGenerate} disabled={busy} startIcon={<RefreshIcon />}>
                    Generează altul
                  </Button>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Nu ai un cod activ. Generează unul și introdu-l în profilul tău SRBHub.
              </Typography>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={busy}
                startIcon={<KeyIcon />}
              >
                {busy ? 'Se generează...' : 'Generează cod de legătură'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Starea legăturii
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              label={isLinked ? 'Conectat' : isRevoked ? 'Revocat' : 'Neconectat'}
              color={isLinked ? 'success' : isRevoked ? 'warning' : 'default'}
            />
            {student?.external_user_id && (
              <Typography variant="body2" color="text.secondary">
                ID SRBHub: {student.external_user_id}
              </Typography>
            )}
            {student?.linked_at && (
              <Typography variant="body2" color="text.secondary">
                Legat la: {new Date(student.linked_at).toLocaleString('ro-RO')}
              </Typography>
            )}
            {isLinked && (
              <Tooltip title="Revocă legătura">
                <span>
                  <Button
                    color="error"
                    size="small"
                    startIcon={<LinkOffIcon />}
                    onClick={handleRevoke}
                    disabled={busy}
                  >
                    Revocă
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mt: 3 }}>
        Pași: generezi codul aici, îl introduci în SRBHub (profil → Leagă SRBCredite), apoi creditele din
        cluburile finalizate se importă automat.
      </Alert>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Container>
  );
}
